/**
 * Every explanatory string in this prototype, in one file.
 *
 * Copy lives here rather than beside the components that render it so it can be
 * read and edited as copy: open this file next to the running prototype and a
 * save shows up in place. Components import from here and declare no strings of
 * their own, so a term can't end up defined two ways in two files without that
 * being visible right here.
 *
 * Two conventions appear inside the strings themselves:
 *
 *   **bold**   emphasises a span
 *   {name}     interpolates a figure the component supplies
 *
 * Both are rendered by `renderCopy`, or by `fillCopy` where the result has to be
 * a plain string rather than markup. Most strings need neither and are rendered
 * directly; only the modal, the lift tags, and the metric footers use them.
 *
 * The voice is uniform across all of these, which is what keeps a hundred-odd
 * tooltips reading as one product rather than a hundred authors:
 *
 *   - Definitions are noun phrases, not sentences about the reader: "Revenue
 *     from all orders placed within 14 days of a click", not "This shows you
 *     the revenue from...". A second sentence carries the caveat or the
 *     arithmetic.
 *   - Faire is named where a sentence needs an actor: "Faire attributes a
 *     conversion to an ad click". "We" is never used.
 *   - "you" and "your" belong to the brand reading the page: your ads, your
 *     budget, retailers that have never ordered from you.
 *   - Customers are acquired or reorder *through* ads, never *via* them.
 *   - Forecasts are impersonal: "Estimated total revenue over 12 months",
 *     "are expected to bring in". Never "we expect".
 *   - Immediate ad sales, Continued ad sales, and First-year ad sales keep
 *     their capital when a string points at the column by name.
 *
 * Apostrophes are ASCII throughout.
 *
 * Not yet centralized: table column labels and filter option labels.
 */
export const COPY = {
  /** Strings used by more than one tab, so a term can't drift between them. */
  shared: {
    /**
     * The gray cells in the forward-looking columns on a reorder-ad row: the
     * cohort table's Continued ad sales, and Lifetime sales and Lifetime orders
     * in both the cohort and Customers tables. One reason for all of them,
     * because they fail for one reason: the ad bought a single order and can't
     * claim what surrounds it.
     */
    reorderNotApplicable:
      "Doesn't apply to reorder ads. A reorder ad's credit stops at the reorder itself, so there's no continued or lifetime figure to attribute to it.",

    /**
     * The four per-retailer columns the Customers table and both versions of
     * the cohort table put the same headers on. They sit here rather than in
     * either tab's block because a reader hovering Lifetime sales on the cohort
     * drilldown is asking exactly what a reader hovering it on the Customers
     * tab is, and a definition kept in three places is a definition that
     * drifts.
     */
    columns: {
      /**
       * The cell shows a store type and says nothing about where it came from,
       * and the two sources aren't equally firm: the retailer declared it, or
       * Faire inferred it. Naming the fallback openly costs a long second
       * sentence and buys the thing a brand needs before it segments spend by
       * store type, which is knowing which rows rest on a guess.
       */
      storeType:
        'Retailer-selected store type. For retailers that haven\'t selected a store type or chose "Other," Faire predicts the closest match based on information about their business and how similar stores describe themselves to help you better understand your customers.',
      /**
       * Which order carries the date depends on the ad type, and that is the
       * one thing about the column the cells can't show. Leading on the
       * conversion rather than on either order hangs both readings off a single
       * noun phrase, so neither ad type reads as the special case.
       */
      orderDate:
        "Date of conversion: for a new customer ad, the order that converted the retailer; for a reorder ad, the reorder itself.",
      /**
       * Paired with `lifetimeSales` and deliberately the same sentence with one
       * noun swapped: the two columns sit side by side over the same history
       * and differ only in what they count, so reading differently would imply
       * they measure different windows.
       *
       * The caveat names new customer ads rather than promoted listings because
       * that is the line the column actually draws: a reorder ad is a promoted
       * listing too, so the wider term would claim a figure for every row while
       * half of them come back gray. Naming the narrower one keeps the header
       * and `reorderNotApplicable` telling one story — which ads produce a
       * lifetime figure at all, and why this row hasn't got one.
       */
      lifetimeOrders:
        "Total orders from this customer. Lifetime orders are only tracked for customers acquired through new customer ads.",
      /** Same sentence as `lifetimeOrders`, for the reason given there. */
      lifetimeSales:
        "Total revenue received from this customer. Lifetime sales are only tracked for customers acquired through new customer ads.",
    },

    /**
     * The forecast explainer, reachable from every figure that is partly
     * projected: the cohort view's first-year metric, and the Overview's
     * first-year ad sales and first-year ROAS columns and legend.
     *
     * Shared rather than owned by one of them because those figures move on
     * their own, and a reader who asks why is asking the same question
     * wherever they happen to be standing. One answer, one place to fix it.
     */
    forecast: {
      /**
       * Also the modal's trigger everywhere, so link and dialog stay paired.
       * The dialog title carries the possessive and this one deliberately
       * doesn't: a trigger reads tighter without it, a title warmer with it.
       */
      link: "How we forecast first-year ad sales",
      modal: {
        title: "How we forecast your first-year ad sales",
        confirm: "Got it",
        /**
         * A blank line starts a new paragraph. Rewrite it as prose; add or
         * remove paragraphs freely.
         *
         * Ordered as the reader's questions arrive: what is being forecast,
         * where the starting number comes from, why it keeps moving, and only
         * then the reassurance that moving is the point. The closing paragraph
         * is labelled because that last question is the one people arrive with
         * and it should be findable without reading the three above it.
         */
        body: `
When you acquire a customer through a new customer ad, we forecast how much they're likely to order from you in their first 12 months.

We base this on your own history: we look at the customers you've already acquired who've been with you for at least a year, and take their average first-year order volume. That gives us a realistic starting point for what a new customer is worth to you.

From there, the forecast updates every month. As a new customer places real orders, we fold those actuals into the estimate and re-forecast the rest of their first year. So the number sharpens over time — starting as an informed estimate and becoming more grounded in reality each month, until it's a real, actual figure at the 12-month mark.

**Why it changes:** More data means a better forecast. Rather than locking in a day-one guess, we keep the number honest by updating it as we learn how your customers actually behave.
`,
      },
    },
  },

  overview: {
    heading: "Performance overview",
    /**
     * Just the frame. The refresh cadence and the promotions caveat used to sit
     * here too, but they describe the month table's figures specifically, so
     * they moved down to it — a reader hitting them at the top of the page has
     * no columns in front of them yet.
     */
    subhead: "An overview of your Promoted Listings performance.",

    kpi: {
      /**
       * Card labels. Three of the four say "Total" because they roll the
       * selected range up into one figure. First-year ROAS is a ratio, so
       * "Total" would claim something it isn't.
       */
      totalAdSalesTitle: "Total ad sales",
      adSpendTitle: "Total ad spend",
      conversionsTitle: "Total conversions",
      firstYearRoasTitle: "First-year ROAS",

      /**
       * The headline figure is a sum of two ad types with different attribution
       * windows, and that is the one thing the number itself cannot show: a
       * dollar of new customer ad sales can arrive up to a year after the click,
       * while a dollar of reorder ad sales is one order and done. Naming both
       * windows in the parentheses is what stops a reader reconciling this card
       * against the two footers beneath it and concluding the arithmetic is off.
       *
       * The rest of each definition belongs to the ad types dialog, so the
       * tooltip ends in the link rather than growing a third sentence. Rendered
       * through `adTypesTooltip`, which requires `interactive` on HeaderTooltip.
       */
      totalAdSales:
        "Total revenue from all orders attributed to your promoted listings. This includes new customer ad sales (every order a new customer places within 12 months of their first) plus reorder ad sales (the single order placed by an existing customer).",
      adSpend: "Total amount spent on promoted listings.",
      /**
       * Named the product rather than the ad types, unlike
       * `customers.kpi.conversions.all` one tab over, which still defines the
       * same metric by naming the two ads that produce it. They no longer match
       * word for word, so edit them independently.
       */
      conversions:
        "Total new customer acquisitions and reorder conversions from promoted listings.",
      firstYearRoas:
        "Overall first-year return on Faire advertising. Calculated by dividing first-year ad sales from all advertising months by total ad spend.",

      /**
       * The two-line split beneath each figure, shown only under All ad types.
       * `{value}` is the figure the card supplies; these are filled by
       * `fillCopy` rather than `renderCopy`, since a footer is a plain string.
       *
       * Sales and spend name the ad type that earned or consumed the money.
       * Conversions and ROAS name what converted instead, because beside a count
       * "from new customer ads" reads as a source rather than a total.
       */
      footers: {
        adSalesNew: "{value} from new customer ads",
        adSalesReorder: "{value} from reorder ads",
        adSpendNew: "{value} on new customer ads",
        adSpendReorder: "{value} on reorder ads",
        conversionsNew: "{value} new customers acquired",
        conversionsReorder: "{value} reorder conversions",
        firstYearRoasNew: "{value} projected first-year ROAS on new customers",
        firstYearRoasReorder: "{value} ROAS on reorder conversions",
      },
    },

    monthTable: {
      title: "Monthly ad performance",
      /**
       * Everything about this table a reader can't get from the column headers:
       * which month the rows are grouped by, when each column refreshes, and
       * that sales are net of promotions. The refresh cadence and the promotions
       * caveat used to sit in a paragraph under the title; they were the same
       * two sentences, so they moved in here rather than being restated beside
       * it. This is now the only place the click-month rule is stated, so it
       * can't be dropped to save space.
       */
      titleTooltip:
        "Rows are grouped by the month of the ad click, not the month the order arrived. Impressions, clicks, conversions, and ad sales are updated daily, while ad spend and utilization are updated weekly. All ad sales reflect your revenue after customer promotions have been applied.",

      /** Shared word-for-word with the sales-and-spend charts' Ad spend legend item. */
      adSpend: "Total amount spent on promoted listings each month.",
      conversions:
        "New customer acquisitions and reorder conversions from promoted listings each month.",
      firstYearAdSales:
        "Forecasted total revenue over 12 months from the customers acquired in each month. The estimate becomes more accurate as customers place additional orders, and after 12 months it's replaced by actual first-year sales.",
      /** Shared word-for-word with the ROAS chart's Immediate legend item. */
      firstOrderRoas:
        "Immediate return on Faire advertising. Calculated by dividing each month's immediate ad sales by ad spend.",
      /**
       * Word-for-word `overview.charts.roas.firstYear`, the same way
       * `firstOrderRoas` matches that chart's Immediate legend item. The legend
       * and the column carry the same label, so they get the same definition —
       * a reader who hovers both shouldn't have to work out whether two
       * different sentences describe two different metrics. Move them together.
       */
      firstYearRoas:
        "First-year return on Faire advertising. Calculated by dividing each month's first-year ad sales by ad spend. The estimate becomes more accurate as the newly acquired customers place more orders, and is replaced by their actual first-year ROAS after 12 months.",

      /**
       * The reorder-ads readings of the two first-year columns. Both columns now
       * report a figure under that filter rather than going blank: the metric is
       * counted for reorder ads under All ad types too, it just has nothing
       * beyond the immediate sale in it. So these say what the figure equals
       * instead of saying the column doesn't apply, and they carry no forecast
       * link — there is nothing left to forecast.
       */
      firstYearAdSalesReorder:
        "The same as Immediate ad sales for reorder ads. A reorder ad's sale lands in the month of the ad click and nothing continues past it, so the first year is already complete.",
      firstYearRoasReorder:
        "The same as Immediate ROAS for reorder ads. With no continued ad sales to accumulate, the immediate return is already the final one.",

      /** All ad types shows one budget figure; a single ad type shows its share of it. */
      utilization:
        "Percentage of the month's budget that is expected to be spent, or was spent, on ads.",
      /**
       * The % of budget column, which replaces Utilization once the filter picks
       * one ad type. Named rather than "this ad type's", since the column only
       * ever appears under a filter and the reader shouldn't have to look back
       * up at the chip to know which spend the percentage is of.
       *
       * No `all` reading: under all ad types the column is Utilization instead,
       * so a share of the budget by ad type has nothing to be a share of.
       */
      budgetShare: {
        new: "The amount spent on new customer ads as a percentage of the month's budget.",
        returning:
          "The amount spent on reorder ads as a percentage of the month's budget.",
      },

      /**
       * One sentence for every ad type. The column means the same thing under
       * each of them — revenue from the order the click drove — so narrowing the
       * filter narrows the rows, not the definition.
       *
       * Shared word-for-word with the sales-and-spend charts' Immediate ad sales
       * legend item.
       *
       * The month goes in front of "click" rather than at the end of the
       * sentence, matching `cohort.kpi.immediateAdSales`. Trailing it reads as
       * though the month bounded the order, which is the opposite of the rule
       * the column follows: the click sets the month, and the order it drove
       * counts there even when it arrives in the next one.
       */
      immediateAdSales:
        "Revenue from all orders placed within 14 days of each month's new customer ad click or reorder ad click.",

      /**
       * Hatched cells in the Continued ad sales column under reorder ads — now
       * the only column that goes blank on that filter, since the two first-year
       * columns report the immediate figures instead.
       */
      disabledContinued:
        "Reorder ad sales land in the month of the ad click, so reorder ads have nothing that continues past it.",

      /**
       * The +% tag beside a row's Continued ad sales. Deliberately the same
       * sentence as `cohort.v2.kpi.continuedLiftTag`, which annotates the same
       * figure on the drilldown a click away: one column explaining itself two
       * ways is how a reader concludes the two screens are measuring different
       * things. Edit them together.
       *
       * Word for word identical now that the drilldown names its month too.
       * Both open on the month, then point back at it with "since then" and
       * "this month's" rather than naming it three times in one sentence.
       *
       * {count} and {orders} come from the cohort's additional-order count, with
       * singular/plural chosen by the caller, and the tag drops to a bare
       * percentage when there is no count to name.
       */
      liftTag:
        "The new customers acquired in {month} placed **{count}** more {orders} since then, adding **{pct}%** on top of this month's immediate ad sales.",
    },

    /**
     * The month table's Continued ad sales column, keyed by the ad type filter.
     * All ad types and New customer ads read the same, deliberately: the column
     * means the same thing under both. Only the reorder ads variant needs its
     * own line, since for reorder ads there is nothing to continue.
     *
     * `all` is shared word-for-word with the sales-and-spend charts' Continued
     * ad sales legend item; the forecast cut of that chart splits it and keeps
     * "already" on its actual half. Edit them together.
     */
    continuedAdSales: {
      all: "Revenue from all orders placed by each month's new customers within 12 months of their first order.",
      new: "Revenue from all orders placed by each month's new customers within 12 months of their first order.",
      returning:
        "Does not apply to reorder ads. Faire does not take credit for orders placed by a retailer after a reorder ad sale.",
    },

    adTypesModal: {
      /**
       * Also the accessible name of the info button on the ad type filter that
       * opens the dialog, so what a screen reader announces and what the dialog
       * is titled are the same sentence.
       */
      title: "What are new customer ads and reorder ads?",
      /**
       * The dialog's other trigger, inside the Total ad sales tooltip. It sits
       * with the dialog it opens, the way `shared.forecast.link` does, so the
       * two can't drift into naming the subject differently.
       *
       * Not the title reused: a question works as a heading but reads as one
       * more thing to answer when it's the last line of a tooltip, where the
       * reader wants to be told there is more and where it is.
       */
      link: "Learn about the different ad types",
      confirm: "Got it",
      /**
       * A blank line starts a new paragraph. Rewrite it as prose; add or remove
       * paragraphs freely.
       *
       * The 14-day window applies to both ad types, so it's stated once up top
       * rather than repeated in each definition. Each term then sits at the
       * front of its own paragraph, since the terms are what the reader came
       * for.
       */
      body: `
Faire attributes a conversion to an ad click when the retailer orders within 14 days of the click. The sale counts toward the month of the click, even when the order itself arrives in the following month. Which ad gets the credit depends on whether they've ordered from you before.

**New customer ads** reach retailers that have never ordered from you. To reflect the long-term value of wholesale advertising, Faire counts every order an ad-acquired customer places within 12 months of their first order as an ad sale.

**Reorder ads** reach retailers that have ordered from you before. Here, Faire only counts that reorder as an ad sale.

The two ad types never compete for the same retailer. After a new customer's first order, they do not see a reorder ad for 12 months. And once a retailer orders from a reorder ad, they won't see another reorder ad for 14 days.
`,
    },

    charts: {
      /** Three charts share this title and differ only in their description. */
      salesAndSpendTitle: "Total ad sales and ad spend",

      /**
       * The hover panel's footer action, opening the drilldown for the month the
       * panel describes. One word: the panel's heading already names the month,
       * so "Explore January 2026" would say it twice, and the button has to fit
       * a panel sized by its figures.
       */
      explore: "Explore",

      /**
       * This chart's legend labels are the month table's column headers, so the
       * three tooltips below are word-for-word `monthTable.immediateAdSales`,
       * `continuedAdSales.all` and `monthTable.adSpend`. A reader who hovers a
       * legend swatch and then the column of the same name is asking one
       * question, and two wordings would read as two metrics. Edit them
       * together — the same pairing `firstOrderRoas` has with `roas.immediate`.
       */
      actuals: {
        description:
          "Immediate ad sales and ad spend from new customer ads and reorder ads in each month, as well as the continued ad sales from the new customers acquired in that month.",
        /** Reorder ads have no cohort maturing, so the chart drops those series. */
        descriptionReorder:
          "Immediate ad sales and ad spend from reorder ads in each month.",
        totalLabel: "Total ad sales",
        immediate:
          "Revenue from all orders placed within 14 days of each month's new customer ad click or reorder ad click.",
        continued:
          "Revenue from all orders placed by each month's new customers within 12 months of their first order.",
        spend: "Total amount spent on promoted listings each month.",
      },

      /**
       * The other cut of the same chart, so it carries the same table-column
       * tooltips as `actuals` above — see the note there. The one departure is
       * `continued`: this chart splits that column into an actual half and a
       * forecasted half, so its actual half keeps "already" to tell it apart
       * from the item directly below it. Otherwise it is the table's sentence.
       */
      forecast: {
        description:
          "The immediate ad sales, the continued ad sales earned so far, the continued ad sales still forecasted before the 12-month mark, and ad spend in each month.",
        firstYearLabel: "First-year ad sales",
        /**
         * The label doesn't say "remainder": it sits directly beneath
         * "Continued ad sales (actual)", and actual-plus-forecast already reads
         * as the two halves of the total.
         */
        remainderLabel: "Continued ad sales (forecasted)",
        immediate:
          "Revenue from all orders placed within 14 days of each month's new customer ad click or reorder ad click.",
        continued:
          "Revenue from all orders each month's new customers have already placed within 12 months of their first order.",
        remainder:
          "Forecast of the continued ad sales still expected from these customers between now and 12 months after their first order.",
        spend: "Total amount spent on promoted listings each month.",
      },

      cumulative: {
        title: "Total ad sales and ad spend, cumulative",
        description:
          "The combined immediate ad sales, continued ad sales (both actual so far and forecasted), and ad spend for all of your customers, across every month and accumulated as each month's customers mature.",
        firstYearLabel: "First-year ad sales",
        continuedForecastLabel: "Continued ad sales (forecasted)",
        immediate:
          "Cumulative revenue from all orders placed within 14 days of each month's new customer ad clicks or reorder ad clicks.",
        forecast:
          "Cumulative forecast of the continued ad sales expected within 12 months of each customer's first order.",
        actual:
          "Cumulative revenue from the orders these customers placed after the one that acquired them, within 12 months of that first order.",
        spend:
          "Total amount spent on ads to acquire these customers. It is flat across maturity because the spend all lands in the acquisition month.",
      },

      roas: {
        title: "Return on ad spend",
        description:
          "Compares the immediate and first-year return on your Faire ad spend, grouped by the month each customer was acquired. Use it to see how the long-term value of advertising builds beyond the immediate return.",
        descriptionReorder:
          "The return on each month's ad spend. Reorder ad sales land in the month of the ad click, so this return is final rather than a figure that keeps growing.",
        /**
         * Word-for-word `overview.monthTable.firstOrderRoas`. The chart legend
         * and the table column carry the same label, so they get the same
         * definition — a reader who hovers both shouldn't have to work out
         * whether two different sentences describe two different metrics. Move
         * them together.
         */
        immediate:
          "Immediate return on Faire advertising. Calculated by dividing each month's immediate ad sales by ad spend.",
        /**
         * Parallel to `immediate` above: same opening shape, same arithmetic
         * sentence, then the caveat that this one is an estimate until the
         * cohort is twelve months old.
         */
        firstYear:
          "First-year return on Faire advertising. Calculated by dividing each month's first-year ad sales by ad spend. The estimate becomes more accurate as the newly acquired customers place more orders, and is replaced by their actual first-year ROAS after 12 months.",
      },

      bySource: {
        title: "Total ad sales by order month",
        description:
          "Revenue attributed to your ads, including new customer ads, reorder ads, and continued ad sales from the customers your new customer ads acquired in previous months.",
        newAds:
          "Revenue from first orders placed by customers acquired through new customer ads in the selected month.",
        reorderAds:
          "Revenue from orders placed through reorder ads in the selected month.",
        previousMonths:
          "Continued ad sales from customers acquired in earlier months, attributed to the selected order month.",
        /** This chart's hover rows are sentences rather than series labels. */
        hoverNewAds: "{amount} in sales from new customer ads this month",
        hoverReorderAds: "{amount} in sales from reorder ads this month",
        hoverPreviousMonths:
          "{amount} in sales from customers acquired through ads in previous months",
      },
    },
  },

  customers: {
    heading: "Customers",
    /**
     * Names the product rather than the two ad types. The tab lists both kinds
     * of conversion and its Ad type column already splits them, so the subhead
     * doesn't have to.
     */
    subhead:
      "An overview of the customers you've converted through promoted listings.",

    kpi: {
      /**
       * The three filter states of the Total conversions card. `all` is `new`
       * and `returning` joined word for word, so a reader moving the filter
       * watches one total split rather than reading three sentences and
       * wondering whether they describe three metrics.
       *
       * That join is why the second half counts reorders rather than the
       * retailers who placed them, and it matches the figure: a retailer who
       * reorders twice in the range is two conversions, not one customer.
       * Naming customers on both sides would read tidier and be wrong.
       *
       * `overview.kpi.conversions` carries `all` word for word on the Overview
       * tab's version of this card. Edit them together.
       */
      conversions: {
        all: "Customers acquired through new customer ads and reorders placed through reorder ads.",
        new: "Customers acquired through new customer ads.",
        returning: "Reorders placed through reorder ads.",
      },
      orders: {
        all: "Every order placed by the customers your ads acquired or brought back, their reorders included.",
        new: "Every order placed by the customers your new customer ads acquired, their reorders included.",
      },
      avgOrders:
        "The average number of orders placed by each customer acquired through new customer ads.",
      avgValue: {
        new: "The average ad sales from each customer acquired through new customer ads.",
        returning:
          "The average ad sales from each reorder placed through reorder ads.",
      },
      avgCost: {
        new: "New customer ad spend divided by the number of customers acquired.",
        returning: "Reorder ad spend divided by the number of reorders placed.",
      },
    },

    table: {
      heading: "Ad customers",
      /**
       * Says who is in the list and what order it's in — the two things that
       * decide whether a reader trusts what they're scanning. The sort clause
       * describes the default the header ships with (Order date, descending);
       * it stops being true if that column's sort is ever made interactive.
       *
       * Both halves name a customer, unlike the conversions card above, because
       * the rows are retailers: one line each, however many reorders sit behind
       * it. The second half is in the ad's voice for that reason — a reorder ad
       * brings a retailer back — which is also what keeps it off "reordered
       * through reorder ads". "By" is the agent of the passive here, not the
       * "via" the file bans.
       */
      headingTooltip:
        "Customers acquired through new customer ads or brought back by reorder ads, sorted by most recent ad-attributed order.",
      /**
       * A noun phrase rather than a sentence about the list, so it reads like
       * the other column definitions. Naming both values costs a clause and
       * buys what the cells can't carry: the labels say which ad ran, not what
       * it did, and acquiring a customer and bringing one back are the whole
       * difference between the two rows.
       */
      adType:
        "The ad type that converted a retailer; either a new customer ad that acquired a customer or a reorder ad that brought back an existing one.",
      empty: "No customers match your filters.",
    },

    storeTypeChart: {
      title: "Monthly ad performance by store type",
      titleTooltip:
        "Data for the current month is excluded until it's fully closed out, so recent activity may lag by up to one month.",
      boostedDisabled: "Available once you're boosting specific store types.",
    },

    storeTypeTable: {
      title: "Cumulative ad performance by store type",
      titleTooltip:
        "Data for the current month is excluded until it's fully closed out, so recent activity may lag by up to one month.",
    },

    /**
     * The expand toggle the two store-type cards share. Side by side they split
     * the page 5/7, which is tight for seven columns and a legend; expanding
     * hands one of them the full width without asking the reader to resize
     * anything. Verbs rather than states: the label says what the click does.
     */
    storeTypeCards: {
      expand: "Expand to full width",
      collapse: "Collapse",
    },
  },

  products: {
    heading: "Products",
    subhead:
      "How your individual products are performing in Promoted Listings, including impressions, clicks, and click-through rates.",
  },

  cohort: {
    /**
     * "by month", not "by conversions month" — the swap away from "cohort"
     * doesn't survive as a modifier. Nothing is lost: the page is reached by
     * clicking a month and every figure on it is scoped to that month, so the
     * shorter form says the same thing. Identical to `v2.heading` now.
     */
    heading: "Ad conversions by month",
    subhead:
      "The performance of your ad conversions from {month} over the 12 months that follow.",

    kpi: {
      /**
       * `customers.kpi.conversions.all` with the cohort's scope hung off the
       * end. The trailing clause is the only difference and is the entire
       * reason this key isn't the other one; keep the definition ahead of it in
       * step with that key, and keep the clause last so the definition reads
       * the same on every surface before the scope narrows it.
       */
      conversions:
        "Customers acquired through new customer ads and reorders placed through reorder ads, in {month}.",
      adSpend:
        "Total amount spent on ads in {month}. Ad spend is updated weekly and finalized on the 4th of the following month.",
      immediateAdSales:
        "Revenue from all orders placed within 14 days of {month}'s new customer ad clicks or reorder ad clicks.",
      continuedTitle: "Continued ad sales",
      /**
       * `overview.continuedAdSales.all` with the month named instead of "each
       * month", so the drilldown card and the Overview column a click apart
       * define the figure the same way. Edit them together.
       */
      continuedAdSales:
        "Revenue from all orders placed by {month}'s new customers within 12 months of their first order.",

      firstYearTitle: "First-year ad sales",
      /**
       * Qualified only while part of the figure is still projected. Once the
       * cohort has its full 12 months the number is settled, and carrying
       * "(forecasted)" past that point would have a reader discounting a figure
       * that is final.
       */
      firstYearForecastTitle: "First-year ad sales (forecasted)",
      firstYearAdSales:
        "Total revenue over 12 months from the customers acquired in {month}. These conversions have passed the 12-month mark, so the figure is final.",
      /**
       * `overview.monthTable.firstYearAdSales` with the month named instead of
       * "each month", so the drilldown card and the Overview column a click
       * apart define the figure the same way. Shared with the two cohort chart
       * legend items carrying this label. Edit them together.
       */
      firstYearAdSalesForecast:
        "Forecasted total revenue over 12 months from the customers acquired in {month}. The estimate becomes more accurate as customers place additional orders, and after 12 months it's replaced by actual first-year sales.",
    },

    /**
     * Both name the month once, at the front, and refer back to it after. They
     * used to open on "this month" and close on the month by name, which read
     * as two different months to anyone not already sure they were the same.
     */
    liftTag: {
      realized:
        "The customers you acquired in {month} have brought in **{amount}** in additional orders since then, which is a **{pct}% lift** over the Immediate ad sales.",
      forecast:
        "The customers you acquired in {month} are expected to bring in **{amount}** in additional orders within 12 months, which would be a **{pct}% lift** over the Immediate ad sales.",
    },

    chart: {
      salesTitle: "Total ad sales",
      ordersTitle: "Total orders",
      salesToDate:
        "Total ad-attributed revenue from {month} conversions through the selected month.",
      /** Same label as the KPI card, so same sentence — see `kpi.firstYearAdSalesForecast`. */
      salesForecast:
        "Forecasted total revenue over 12 months from the customers acquired in {month}. The estimate becomes more accurate as customers place additional orders, and after 12 months it's replaced by actual first-year sales.",
      adSpend: "Total amount spent on ads in {month}.",
      ordersToDate:
        "Total ad-attributed orders from {month} conversions through the selected month.",
      ordersForecast:
        "Estimated additional orders from {month} conversions over their first 12 months.",
    },

    table: {
      heading: "Conversions this month",
      /**
       * `overview.monthTable.conversions` scoped to the one month, so the column
       * a reader clicked and the table they landed on define conversions the
       * same way. Shared: v2 reads this too, since its table counts the same
       * thing under a shorter heading. Edit them together.
       */
      headingTooltip:
        "New customer acquisitions and reorder conversions from promoted listings in {month}.",
      /** "Conversions" rather than "customers": a reorder row isn't a new one. */
      exploreAll: "Explore all conversions",
      continuedSales:
        "Revenue from the orders this customer placed after the one that acquired them, within 12 months of that first order.",
      liftTag:
        "This customer has brought in **{amount}** in continued ad sales since their first order, which is a **{pct}% lift** over their Immediate ad sales.",
      /** One state now that the table lists both ad types together. */
      empty: "No ad conversions this month.",
    },

    /**
     * The v2 drilldown. Only the strings that actually differ live here; v2
     * reads everything else from the block above, so a fix to a shared
     * definition can't land in one version and miss the other.
     *
     * The split exists because v2 reframes the page around the first-year
     * total: the headline metrics close on it, the chart plots the three bands
     * that add up to it, and the table drops the intermediate money columns.
     */
    v2: {
      /** Identical to `cohort.heading`; see the note there. */
      heading: "Ad conversions by month",
      /**
       * Names both halves of the page in the order they appear: the month's own
       * conversions in the table, and the first year those conversions go on to
       * produce in the metrics and chart above it.
       */
      subhead:
        "Explore the ad conversions from this month, and the first-year performance of customers you acquired through new customer ads.",

      kpi: {
        /**
         * v2 adds the sum as its own metric, so the two figures above it read
         * as an equation rather than as two unrelated numbers. Its footers
         * restate the addends for exactly that reason.
         */
        totalTitle: "Total ad sales",
        totalAdSales:
          "Immediate ad sales plus continued ad sales: everything {month}'s ad conversions have brought in so far.",
        /** Singular/plural is chosen by the caller from the order count. */
        continuedFooter: "From {count} additional {orders}",
        /**
         * The +% tag beside Continued ad sales. The value above it is dollars
         * and the tag is a percentage, so the sentence has to say what the
         * percentage is a share of; naming Immediate ad sales points at the
         * card next to it rather than at an arithmetic a reader would have to
         * reconstruct. Leading on the orders instead of the dollars is what
         * separates this from the value it annotates: the reorders are the
         * reason the figure moved, and the count is the only place on the card
         * that says how many there were.
         *
         * {count} and {orders} come from the same additional-order count as
         * `continuedFooter`, with singular/plural chosen by the caller; {month}
         * is the short cohort label, matching the chart axes and the other
         * tooltips on this page rather than the long form in the heading.
         *
         * Paired with `overview.monthTable.liftTag`, which puts this sentence on
         * the same figure in the row this page opens from. It differs only where
         * a table row forces it to; the two are kept in step by hand, so edit
         * both.
         */
        continuedLiftTag:
          "The new customers acquired in {month} placed **{count}** more {orders} since then, adding **{pct}%** on top of this month's immediate ad sales.",
      },

      chart: {
        /**
         * Names the destination rather than the plotted series: the line climbs
         * toward the first-year total, and that total is the figure the metrics
         * beside it close on. The month is repeated here even though the page
         * heading states it, because the chart is the one element a reader
         * screenshots out of context.
         */
        title: "First-year ad sales from {month} conversions",
        /**
         * Hung off the title, so it covers the chart as a whole rather than one
         * series: the solid line and the dashed one are the same measure at two
         * points in time, and that relationship is the thing neither legend
         * item can state on its own.
         */
        titleTooltip:
          "Total ad sales placed by customers acquired in {month} to date. The dashed line represents where we forecast your total ad sales will be at the 12-month mark.",
        /**
         * The realized line: everything those conversions have brought in so
         * far, not one month's worth. Named "Total" in the legend for that
         * reason.
         *
         * Word-for-word `v2.kpi.totalAdSales`. The legend item and the headline
         * card on this page carry the same label, so they get the same
         * sentence — a reader who hovers both shouldn't have to work out
         * whether the line and the card are two different totals. Edit them
         * together.
         */
        salesToDate:
          "Immediate ad sales plus continued ad sales: everything {month}'s ad conversions have brought in so far.",
        /**
         * The dotted tail and its end marker. Separate from the definition
         * above because a reader hovering the forecast is asking a different
         * question than one hovering the solid line.
         *
         * Same label as the KPI card, so same sentence — see
         * `kpi.firstYearAdSalesForecast`.
         */
        salesForecast:
          "Forecasted total revenue over 12 months from the customers acquired in {month}. The estimate becomes more accurate as customers place additional orders, and after 12 months it's replaced by actual first-year sales.",
        adSpend: "Total amount spent on ads in {month}.",
      },

      table: {
        /**
         * Shorter than v1's "Conversions this month" because v2 puts a search
         * box directly under it: the heading names the list, and the month is
         * already stated twice above.
         */
        heading: "Conversions",
        searchPlaceholder: "Search by store name",
        /**
         * Replaces v1's "Immediate ad sales" column. The table lists one order
         * per row, so naming the row's own amount is plainer than naming the
         * metric that aggregates it — and it stops the column from reading as
         * a total.
         */
        orderAmount:
          "What this retailer spent on the order the ad drove. {month}'s Immediate ad sales is the sum of this column.",
        /** Distinct from v1's: this table filters, so a reader can empty it. */
        empty: "No conversions match your filters.",
      },
    },
  },

  settings: {
    /**
     * The panel that used to be "Target audience".
     *
     * Local to this prototype on purpose. The module it replaces lives in
     * pages/Ads2DControlsAlphaBeta/shared.tsx and is rendered by five other
     * prototypes, which keep the audience framing; only this one reframes the
     * choice as which kind of ad runs.
     *
     * The two option names are the ones the Overview's ad type filter uses, so
     * the setting and the figures it moves are named the same thing in both
     * places.
     */
    adType: {
      heading: "Ad type",
      /**
       * Diverges from the audience version, which asked which retailers to
       * reach. The options are campaign types, so the campaign is what's being
       * chosen and the audience is the basis for choosing — the other way round
       * from how the shared string read.
       */
      subhead:
        "Choose which types of ad campaigns you want to run based on who you want to reach.",
      both: {
        title: "New customer ads and reorder ads",
        description:
          "Acquire new customers and drive reorders from existing customers.",
        tag: "Recommended",
      },
      new: {
        title: "New customer ads only",
        description: "Acquire new customers only.",
      },
    },
    budgetLimitingTag: "Budget is limiting reach",
    productBoosting:
      "Since your budget is tight, Faire is focusing more of it on your new products, which may mean your other products show up less often. A higher budget could help you reach both without tradeoffs.",
    preferredStoreTypes:
      "Since your budget is tight, Faire is focusing more of it on your preferred store types, which may mean your other store types show up less often. A higher budget could help you reach both without tradeoffs.",
  },
} as const;
